<script lang="ts">
  import { getModalStore, getToastStore } from '@skeletonlabs/skeleton'
  import { onMount, type SvelteComponent } from 'svelte'

  import { prepareInputValuesForQuery } from './storage-configs-store'
  import { StorageType, type StorageType as StorageTypeValue } from './storage-configs-store'
  import StorageFormFields from './StorageFormFields.svelte'
  import {
    createSaveHuggingFaceTokenMutation,
    persistSavedHuggingFaceToken,
  } from '@routes/huggingface-tokens/huggingface-tokens-store'
  import { loggedInStore } from '@stores/logged-in-store'
  import { setRestoreState } from '@utils/modal-restore'
  import { queryClient } from '@utils/svelte-query-client'

  import Icon from '$lib/components/Icon.svelte'

  interface Props {
    /** Exposes parent props to this component. */
    parent: SvelteComponent
  }

  let { parent }: Props = $props()

  const modalStore = getModalStore()
  const toastStore = getToastStore()
  const readOnly = $modalStore[0]?.meta?.readOnly || false
  const restoreKey: string | undefined = $modalStore[0]?.meta?.restoreKey
  const expectedSaveTokenErrors = new Set([
    'A custom HuggingFace token is required to save it.',
    'Token name is required when saving a HuggingFace token.',
  ])

  interface StorageFormRestoreState {
    selectedStorageType: StorageTypeValue | null
    storageConfigName: string
    inputValues: { [key: string]: string | undefined | null }
  }
  const isStorageFormRestoreState = (v: unknown): v is StorageFormRestoreState =>
    !!v && typeof v === 'object' && 'inputValues' in (v as Record<string, unknown>)

  let storageConfig = $modalStore[0].value
  let selectedStorageType = $state(storageConfig?.type || null)
  let storageConfigName = $state(storageConfig?.name || '')
  let currentOrganizationId = storageConfig?.organizationId || $loggedInStore.primaryOrganizationId

  let inputValues = $state<StorageFormRestoreState['inputValues']>({})
  let storageConfigFormElement: HTMLFormElement | undefined = $state()
  const saveTokenMutation = createSaveHuggingFaceTokenMutation(queryClient)

  // Restore previously cached state if present
  onMount(() => {
    if (
      $modalStore[0]?.meta?.restoreState &&
      isStorageFormRestoreState($modalStore[0].meta.restoreState)
    ) {
      const restoreState = $modalStore[0].meta.restoreState
      selectedStorageType = restoreState.selectedStorageType ?? selectedStorageType
      storageConfigName = restoreState.storageConfigName ?? storageConfigName
      inputValues = restoreState.inputValues ?? inputValues
    }
  })

  $effect(() => {
    if (!restoreKey) {
      return
    }
    setRestoreState(restoreKey, {
      selectedStorageType,
      storageConfigName,
      inputValues,
    } satisfies StorageFormRestoreState)
  })

  const persistHuggingFaceTokenIfRequested = async () => {
    if (selectedStorageType !== StorageType.HuggingFace) {
      return undefined
    }
    if (inputValues['saveToken'] !== 'true') {
      return undefined
    }
    return persistSavedHuggingFaceToken({
      shouldSave: inputValues['saveToken'] === 'true',
      token: typeof inputValues['token'] === 'string' ? inputValues['token'] : undefined,
      tokenName: typeof inputValues['tokenName'] === 'string' ? inputValues['tokenName'] : '',
      mutation: $saveTokenMutation,
      onSuccess: (createdTokenId) => {
        inputValues['tokenId'] = createdTokenId.toString()
        inputValues['token'] = null
        inputValues['saveToken'] = null
        inputValues['tokenName'] = ''
        inputValues['authType'] = 'token'
      },
    })
  }

  const submitHandler = async () => {
    if (!storageConfigFormElement?.reportValidity()) {
      return
    }
    if (!selectedStorageType) {
      console.error('Tried to create a storage config without a storage type')
      return
    }

    let createdTokenId: number | undefined
    try {
      createdTokenId = await persistHuggingFaceTokenIfRequested()
    } catch (error) {
      if (!(error instanceof Error) || !expectedSaveTokenErrors.has(error.message)) {
        throw error
      }
      toastStore.trigger({
        message: error.message,
        background: 'variant-filled-error',
        autohide: false,
      })
      return
    }

    const payload = prepareInputValuesForQuery(inputValues, selectedStorageType)
    if (createdTokenId !== undefined && payload.huggingface?.authType === 'token') {
      payload.huggingface.tokenId = createdTokenId
    }
    if (payload.git && storageConfig?.git?.repo?.id) {
      payload.git.repo = {
        id: storageConfig.git.repo.id,
        ...payload.git.repo,
      }
    }
    payload.organizationId = currentOrganizationId
    payload.name = storageConfigName
    payload.type = selectedStorageType

    if (!$modalStore[0].response) {
      console.error("Modal doesn't have a response function")
      return
    }
    $modalStore[0].response({ payload, closeModal: parent.onClose })
  }
</script>

{#if $modalStore[0]}
  <div
    class="card relative h-[31rem] max-h-[80vh] w-[38rem] max-w-[38rem] space-y-4 justify-self-center overflow-y-scroll px-6 md:min-w-[30rem] {readOnly &&
      'pb-2'}"
  >
    <h3 class="h3 sticky top-0 z-10 justify-between bg-surface-50 p-4 dark:bg-surface-800">
      {$modalStore[0].title}
      <button
        class="absolute right-2 top-2 text-surface-600 hover:text-primary-600 dark:text-surface-400 dark:hover:text-primary-300"
        type="button"
        onclick={parent.onClose}
      >
        <Icon icon="iconoir:xmark-circle" />
      </button>
    </h3>
    <span>{$modalStore[0].body}</span>
    <StorageFormFields
      bind:storageFormElement={storageConfigFormElement}
      bind:inputValues
      bind:selectedStorageType
      bind:storageConfigName
      {storageConfig}
      {readOnly}
    ></StorageFormFields>
    {#if !readOnly}
      <footer
        class="modal-footer sticky bottom-0 bg-surface-50 pb-4 pt-2 dark:bg-surface-800 {parent.regionFooter}"
      >
        <div class="variant-filled-primary btn-group w-min">
          <button
            class="variant-filled-primary {parent.buttonPositive}"
            type="submit"
            onclick={submitHandler}
          >
            {parent.buttonTextSubmit}
          </button>
          <button class="variant-filled-error" type="button" onclick={parent.onClose}>
            Cancel
          </button>
        </div>
      </footer>
    {/if}
  </div>
{/if}

<style>
  .card {
    flex-direction: column;
    width: fit-content;
  }
  .card > * {
    margin: 0.5rem 0;
  }
</style>
